// src/pages/shared/ProjectList.jsx

import AdminLayout from "../../layouts/AdminLayout";
import useAuthStore from "../../store/authStore";
import ManagerLayout from "../../layouts/ManagerLayout";
import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  getProjects,
  updateProject,
  getProjectLocations,
} from "../../api/projectService";

// ─── sessionStorage helpers ───────────────────────────────────────────────
// sessionStorage is scoped to the browser tab and is wiped automatically on
// hard-refresh (F5 / Ctrl+R) or when the tab is closed, so filters reset on
// true page reload but survive React Router back-navigation perfectly.
const SS_KEY = "projectList_filterState";

function loadFilters() {
  try {
    const raw = sessionStorage.getItem(SS_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveFilters(state) {
  try {
    sessionStorage.setItem(SS_KEY, JSON.stringify(state));
  } catch {}
}

function clearFilters() {
  try {
    sessionStorage.removeItem(SS_KEY);
  } catch {}
}

// ─── Default filter values ────────────────────────────────────────────────
const DEFAULTS = {
  statusFilter:   "all",
  locationFilter: "all",
  searchQuery:    "",
};

function ProjectList() {

  /* ================= ROLE ================= */
  const role = useAuthStore((state) => state.role);
  const isAdmin = role === "ADMIN";
  const Layout  = isAdmin ? AdminLayout : ManagerLayout;

  /* ================= STATE ================= */
  const saved = loadFilters();

  const [projects,       setProjects]       = useState([]);
  const [statusFilter,   setStatusFilter]   = useState(saved?.statusFilter   ?? DEFAULTS.statusFilter);
  const [locationFilter, setLocationFilter] = useState(saved?.locationFilter ?? DEFAULTS.locationFilter);
  const [searchQuery,    setSearchQuery]    = useState(saved?.searchQuery    ?? DEFAULTS.searchQuery);
  const [locations,      setLocations]      = useState([]);
  const [loading,        setLoading]        = useState(true);

  // ── Persist every filter change to sessionStorage ─────────────────────
  useEffect(() => {
    saveFilters({ statusFilter, locationFilter, searchQuery });
  }, [statusFilter, locationFilter, searchQuery]);

  const handleResetFilters = () => {
    setStatusFilter(DEFAULTS.statusFilter);
    setLocationFilter(DEFAULTS.locationFilter);
    setSearchQuery(DEFAULTS.searchQuery);
    clearFilters();
  };

  const hasActiveFilters =
    statusFilter   !== DEFAULTS.statusFilter ||
    locationFilter !== DEFAULTS.locationFilter ||
    searchQuery    !== DEFAULTS.searchQuery;

  const searchRef = useRef(null);

  /* ================= FETCH — 2 parallel calls, no per-project loops ================= */
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [projectsRes, locationsRes] = await Promise.all([
          getProjects(),
          getProjectLocations(),
        ]);
        setProjects(Array.isArray(projectsRes.data) ? projectsRes.data : []);
        setLocations(locationsRes.data.locations || []);
      } catch (err) {
        console.error("Failed to load:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const STATUS_CHOICES = [
    { key: "PLANNED",     label: "Planned"     },
    { key: "IN_PROGRESS", label: "In Progress" },
    { key: "COMPLETED",   label: "Completed"   },
    { key: "ON_HOLD",     label: "On-Hold"     },
  ];

  /* ================= FILTER + SEARCH ================= */
  const q = searchQuery.trim().toLowerCase();

  const filteredProjects = projects.filter((p) => {
    const matchStatus   = statusFilter   === "all" || p.status === statusFilter;
    const matchLocation = locationFilter === "all" || String(p.location?.id) === locationFilter;
    const matchSearch   = !q ||
      p.project_id.toLowerCase().includes(q) ||
      p.project_name.toLowerCase().includes(q) ||
      (p.location?.name || "").toLowerCase().includes(q);
    return matchStatus && matchLocation && matchSearch;
  });

  /* ================= STATUS UPDATE ================= */
  const handleStatusChange = async (projectId, newStatus) => {
    setProjects((prev) =>
      prev.map((p) => p.project_id === projectId ? { ...p, status: newStatus } : p)
    );
    try {
      await updateProject(projectId, { status: newStatus });
    } catch (err) {
      console.error("Failed to update status:", err);
      const res = await getProjects();
      setProjects(Array.isArray(res.data) ? res.data : []);
    }
  };

  /* ================= ROW COLOR ================= */
  const getRowBackground = (status) => {
    if (status === "COMPLETED") return "bg-emerald-400/40 hover:bg-emerald-400/50";
    if (status === "ON_HOLD")   return "bg-gray-500/30 hover:bg-gray-500/40";
    return "bg-white/10 hover:bg-white/20";
  };

  /* ================= STATUS BADGE FOR CARDS ================= */
  const getStatusBadge = (status) => {
    const map = {
      PLANNED:     "bg-blue-500/20 text-blue-300 border-blue-400/30",
      IN_PROGRESS: "bg-yellow-500/20 text-yellow-300 border-yellow-400/30",
      COMPLETED:   "bg-emerald-500/20 text-emerald-300 border-emerald-400/30",
      ON_HOLD:     "bg-gray-500/20 text-gray-300 border-gray-400/30",
    };
    return map[status] || "bg-white/10 text-white/60 border-white/20";
  };

  /* ================= UI ================= */
  return (
    <Layout>
      <div className="p-3 sm:p-6">
        <div className="w-full max-w-[1200px] mx-auto bg-white/15 backdrop-blur-xl rounded-3xl shadow-2xl p-4 sm:p-8 border border-white/20">

          {/* TITLE */}
          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-center py-3 sm:py-4 rounded-2xl font-bold text-base sm:text-xl mb-4 sm:mb-6 shadow-lg">
            View All Projects
          </div>

          {/* ── TOP BAR ── */}
          <div className="flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-6">

            {/* Row 1: title + filter dropdowns */}
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
              <p className="font-bold text-white text-sm sm:text-base">📁 Projects History</p>

              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <label className="text-white/90 text-xs sm:text-sm font-semibold">Location:</label>
                <select
                  value={locationFilter}
                  onChange={(e) => setLocationFilter(e.target.value)}
                  className="flex-1 min-w-[120px] bg-white/20 border-2 border-white/30 rounded-xl px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-white"
                >
                  <option value="all" className="bg-slate-800">All Locations</option>
                  {locations.map((l) => (
                    <option key={l.id} value={String(l.id)} className="bg-slate-800">
                      {l.name}
                    </option>
                  ))}
                </select>

                <label className="text-white/90 text-xs sm:text-sm font-semibold">Status:</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="flex-1 min-w-[110px] bg-white/20 border-2 border-white/30 rounded-xl px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm text-white"
                >
                  <option value="all" className="bg-slate-800">All Projects</option>
                  {STATUS_CHOICES.map((s) => (
                    <option key={s.key} value={s.key} className="bg-slate-800">
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 2: search bar */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                  <svg className="w-4 h-4 text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                  </svg>
                </div>
                <input
                  ref={searchRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by Project ID, Name or Location..."
                  className="w-full bg-white/10 border-2 border-white/20 hover:border-white/35 focus:border-blue-400 rounded-xl pl-10 pr-10 py-2 sm:py-2.5 text-xs sm:text-sm text-white placeholder-white/35 outline-none transition-colors"
                />
                {searchQuery && (
                  <button
                    onClick={() => { setSearchQuery(""); searchRef.current?.focus(); }}
                    className="absolute inset-y-0 right-3 flex items-center text-white/40 hover:text-white/80 transition-colors text-lg leading-none"
                  >
                    ✕
                  </button>
                )}
              </div>

              {hasActiveFilters && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  onClick={handleResetFilters}
                  className="flex-shrink-0 px-3 sm:px-4 py-2.5 rounded-xl bg-white/10 border-2 border-white/20 hover:bg-white/20 text-white/70 hover:text-white text-xs font-bold transition-all duration-200 whitespace-nowrap"
                  title="Clear all filters"
                >
                  ↺ Reset
                </motion.button>
              )}
            </div>

            {/* Result count hint */}
            {(q || statusFilter !== "all" || locationFilter !== "all") && !loading && (
              <p className="text-white/45 text-xs">
                Showing <span className="text-white/80 font-semibold">{filteredProjects.length}</span> of{" "}
                <span className="text-white/80 font-semibold">{projects.length}</span> projects
              </p>
            )}
          </div>

          {/* ── DESKTOP TABLE (md+) ── */}
          <div className="hidden md:block bg-white/10 backdrop-blur-md rounded-2xl overflow-hidden border border-white/20 shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="bg-white/20 text-sm border-b border-white/30">
                    <th className="py-4 px-3 text-white text-left">Project ID</th>
                    <th className="py-4 px-3 text-white">Project Name</th>
                    <th className="py-4 px-3 text-white">Manager</th>
                    <th className="py-4 px-3 text-white">Location</th>
                    <th className="py-4 px-3 text-white">{isAdmin ? "View Discipline" : "Create Discipline"}</th>
                    <th className="py-4 px-3 text-white">View Progress</th>
                    <th className="py-4 px-3 text-white">Status</th>
                  </tr>
                </thead>

                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="7" className="py-10 text-center text-white/60">
                        Loading projects...
                      </td>
                    </tr>
                  )}

                  {!loading && filteredProjects.length === 0 && (
                    <tr>
                      <td colSpan="7" className="py-10 text-center text-white/40 text-sm">
                        {q || statusFilter !== "all" || locationFilter !== "all"
                          ? "No projects match the selected filters."
                          : "No projects found."
                        }
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    filteredProjects.map((project, index) => (
                      <motion.tr
                        key={project.project_id}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: Math.min(index * 0.015, 0.3) }}
                        className={`text-center border-b border-white/10 ${getRowBackground(project.status)}`}
                      >
                        <td className="py-4 px-3 text-white font-semibold text-left text-sm">
                          {q && project.project_id.toLowerCase().includes(q)
                            ? <HighlightText text={project.project_id} query={q} />
                            : project.project_id
                          }
                        </td>

                        <td className="py-4 px-3 text-white text-sm">
                          {q && project.project_name.toLowerCase().includes(q)
                            ? <HighlightText text={project.project_name} query={q} />
                            : project.project_name
                          }
                        </td>

                        <td className="py-4 px-3 text-white text-sm">
                          {project.project_manager ? (
                            <div className="leading-tight">
                              <div>{project.project_manager.full_name?.split(" ")[0] || "Manager"}</div>
                              <div className="text-yellow-300 font-semibold text-xs">
                                {project.project_manager.employee_id}
                              </div>
                            </div>
                          ) : "—"}
                        </td>

                        <td className="py-4 px-3 text-white/80 text-sm">
                          {project.location
                            ? (
                              <span className="px-2 py-0.5 rounded-lg bg-violet-500/20 text-violet-300 text-xs font-semibold">
                                {q && project.location.name.toLowerCase().includes(q)
                                  ? <HighlightText text={project.location.name} query={q} />
                                  : project.location.name
                                }
                              </span>
                            )
                            : <span className="text-white/30">—</span>
                          }
                        </td>

                        <td className="py-4 px-3">
                          {!isAdmin ? (
                            <Link to={`/manager/discipline-details/${project.project_id}`}>
                              <button className="bg-purple-500 px-4 py-1.5 rounded-xl text-xs text-white font-bold hover:bg-purple-600 transition-colors">
                                Create
                              </button>
                            </Link>
                          ) : (
                            <Link to={`/admin/projects/${project.project_id}/view`}>
                              <button className="bg-gray-500 px-4 py-1.5 rounded-xl text-xs text-white font-bold hover:bg-gray-600 transition-colors">
                                View
                              </button>
                            </Link>
                          )}
                        </td>

                        <td className="py-4 px-3">
                          <Link to={`/${role.toLowerCase()}/projects/${project.project_id}/progress`}>
                            <button className="bg-blue-500 px-4 py-1.5 rounded-xl text-xs text-white font-bold hover:bg-blue-600 transition-colors">
                              View Progress
                            </button>
                          </Link>
                        </td>

                        <td className="py-4 px-3">
                          {!isAdmin ? (
                            <select
                              value={project.status}
                              onChange={(e) => handleStatusChange(project.project_id, e.target.value)}
                              className="bg-white/20 border border-white/30 rounded-xl px-2 py-1.5 text-xs text-white"
                            >
                              {STATUS_CHOICES.map((s) => (
                                <option key={s.key} value={s.key} className="bg-slate-800">
                                  {s.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="text-white text-sm">{project.status}</span>
                          )}
                        </td>
                      </motion.tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── MOBILE CARDS (below md) ── */}
          <div className="md:hidden space-y-3">
            {loading && (
              <div className="py-10 text-center text-white/60">Loading projects...</div>
            )}

            {!loading && filteredProjects.length === 0 && (
              <div className="py-10 text-center text-white/40 text-sm">
                {q || statusFilter !== "all" || locationFilter !== "all"
                  ? "No projects match the selected filters."
                  : "No projects found."
                }
              </div>
            )}

            {!loading && filteredProjects.map((project, index) => (
              <motion.div
                key={project.project_id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.04, 0.3) }}
                className={`border border-white/20 rounded-2xl overflow-hidden shadow-lg transition ${getRowBackground(project.status)}`}
              >
                {/* Card Header */}
                <div className="flex items-start justify-between px-4 py-3 border-b border-white/15 bg-white/10 gap-2">
                  <div className="min-w-0">
                    <p className="text-white font-bold text-sm truncate">
                      {q && project.project_id.toLowerCase().includes(q)
                        ? <HighlightText text={project.project_id} query={q} />
                        : project.project_id
                      }
                    </p>
                    <p className="text-white/70 text-xs truncate">
                      {q && project.project_name.toLowerCase().includes(q)
                        ? <HighlightText text={project.project_name} query={q} />
                        : project.project_name
                      }
                    </p>
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 rounded-lg text-xs font-semibold border ${getStatusBadge(project.status)}`}>
                    {STATUS_CHOICES.find(s => s.key === project.status)?.label || project.status}
                  </span>
                </div>

                {/* Card Body */}
                <div className="p-4 space-y-3">
                  {/* Manager & Location */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/10 rounded-xl p-2.5">
                      <p className="text-white/50 text-xs mb-1">Manager</p>
                      {project.project_manager ? (
                        <>
                          <p className="text-white text-sm font-semibold leading-tight">
                            {project.project_manager.full_name?.split(" ")[0] || "Manager"}
                          </p>
                          <p className="text-yellow-300 text-xs font-semibold">
                            {project.project_manager.employee_id}
                          </p>
                        </>
                      ) : <p className="text-white/40 text-sm">—</p>}
                    </div>
                    <div className="bg-white/10 rounded-xl p-2.5">
                      <p className="text-white/50 text-xs mb-1">Location</p>
                      {project.location
                        ? <span className="px-1.5 py-0.5 rounded-md bg-violet-500/20 text-violet-300 text-xs font-semibold">
                            {q && project.location.name.toLowerCase().includes(q)
                              ? <HighlightText text={project.location.name} query={q} />
                              : project.location.name
                            }
                          </span>
                        : <span className="text-white/30 text-sm">—</span>
                      }
                    </div>
                  </div>

                  {/* Status Select (manager only) */}
                  {!isAdmin && (
                    <div className="bg-white/10 rounded-xl p-2.5">
                      <p className="text-white/50 text-xs mb-2">Update Status</p>
                      <select
                        value={project.status}
                        onChange={(e) => handleStatusChange(project.project_id, e.target.value)}
                        className="w-full bg-white/20 border border-white/30 rounded-lg px-3 py-1.5 text-xs text-white"
                      >
                        {STATUS_CHOICES.map((s) => (
                          <option key={s.key} value={s.key} className="bg-slate-800">
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-2">
                    {!isAdmin ? (
                      <Link to={`/manager/discipline-details/${project.project_id}`}>
                        <button className="w-full bg-purple-500 py-2 rounded-xl text-xs text-white font-bold hover:bg-purple-600 transition-colors">
                          Create Discipline
                        </button>
                      </Link>
                    ) : (
                      <Link to={`/admin/projects/${project.project_id}/view`}>
                        <button className="w-full bg-gray-500 py-2 rounded-xl text-xs text-white font-bold hover:bg-gray-600 transition-colors">
                          View Discipline
                        </button>
                      </Link>
                    )}

                    <Link to={`/${role.toLowerCase()}/projects/${project.project_id}/progress`}>
                      <button className="w-full bg-blue-500 py-2 rounded-xl text-xs text-white font-bold hover:bg-blue-600 transition-colors">
                        View Progress
                      </button>
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

        </div>
      </div>
    </Layout>
  );
}

/* ── Highlight matched substring in text ── */
function HighlightText({ text, query }) {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-300/40 text-white rounded px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

export default ProjectList;
